import { apiFetch } from "@/lib/apiClient";
import type {
  SocialProvider,
  GuestInitResponse,
  GuestStatusResponse,
  GuestSessionError,
  GuestApiEnvelope,
  GuestErrorPayload,
} from "@artha/types";

const unwrapGuestPayload = <T>(payload: GuestApiEnvelope<T> | T): T => {
  if (
    typeof payload === "object" &&
    payload !== null &&
    "data" in payload
  ) {
    return payload.data;
  }

  return payload;
};

export const initGuestSession = async (
  profileUrl: string,
  timezone?: string,
  provider?: SocialProvider,
  countryCode?: string,
): Promise<GuestInitResponse> => {
  const res = await apiFetch<GuestApiEnvelope<GuestInitResponse> | GuestErrorPayload>(
    "/auth/guest/init",
    {
    method: "POST",
    body: JSON.stringify({ profileUrl, timezone, provider, countryCode }),
  });

  if (!res.ok) {
    const errData = res.data as GuestErrorPayload | undefined;
    const code = errData?.error?.code ?? errData?.code ?? "GUEST_INIT_FAILED";
    const message =
      errData?.error?.message ??
      errData?.message ??
      "Failed to start guest session";
    const err = new Error(message) as GuestSessionError;
    err.code = code;
    throw err;
  }

  return unwrapGuestPayload(res.data as GuestApiEnvelope<GuestInitResponse>);
};

export const getGuestStatus = async (): Promise<GuestStatusResponse> => {
  const res = await apiFetch<GuestApiEnvelope<GuestStatusResponse>>(
    "/auth/guest/status",
  );

  if (!res.ok || !res.data) {
    throw new Error("Failed to get guest status");
  }

  return unwrapGuestPayload(res.data);
};
